<svelte:options tag="swc-general-address-email" immutable={true} />

<script>

  import { onMount } from "svelte";
  import { cookieService } from "./helpers/cookie-service";
  import { queryParamService } from "./helpers/query-param-service";
  import { apiService } from "./helpers/api-service";
  import { validationService } from "./helpers/validation-service";
  import Select from "./components/Select.svelte";
  import Input from "./components/Input.svelte";
  import PrimaryButton from "./components/PrimaryButton.svelte";
  import TermsAndConditions from "./components/TermsAndConditions.svelte";
  import Loading from "./components/LoadingSpinner.svelte";
  import ValidationMessage from "./components/ValidationMessage.svelte";
  import FeefoRating from "./components/FeefoRating.svelte";

  export let autofocus;
  export let startslug;
  export let appurl;
  export let apiurl;
  export let ssidkey;
  export let title;
  export let buttontext;
  export let getsignuppropname;
  export let warningheading;
  export let warningcontent;

  const ssidKey = ssidkey;

  let hasInputsSet = false;
  let areAddressesLoading = false;
  let isLoading = true;

  // User input values
  let addressId = "";
  let postcode = "";
  let hasAcceptedMarketingConsent = false;

  let addressValidationMessage = null;
  let postcodeValidationMessage = null;

  let signUp;

  let addresses = [];

  let submitError = null;
  let isPristine = true;

  let hasWarning = Boolean(warningcontent || warningheading);

  const getAddressesForPostcode = async (postcodeToGet) => {
    if (postcodeValidationMessage) {
      addresses = [];
      addressId = "";
      return;
    }
    try {
      const retrievedAddresses = await apiService.getAddresses(postcodeToGet);
      addresses = retrievedAddresses.addresses;
    } catch (error) {
      addresses = [];
    }
  };

  const initAddress = async () => {
    try {
      const addressResponse = await apiService.getAddress(addressId);
      await postcodeChanged(addressResponse.address.postcode);
    } catch (error) {}
  };

  const initSignUp = async () => {
    try {
      const ssid = cookieService.get(ssidKey);

      const affiliateReferralCustomerId
        = queryParamService.get("clickref")
        || cookieService.get("clickref");

      const signUpResponse = await apiService.getSignUp(
        ssid,
        affiliateReferralCustomerId
      );
      signUp = signUpResponse[getsignuppropname];
      cookieService.set(ssidKey, signUp.id, null, "/");
    } catch (error) {}
    const orderRequest = signUp.orderRequest;
    if (orderRequest) {
      // TODO:: This might not be correct
      hasAcceptedMarketingConsent = Boolean(
        orderRequest.marketingConsentAt
      );
      addressId = orderRequest.addressId;
    }
    if (addressId) {
      await initAddress();
    }
    isLoading = false;
  };

  const init = async () => {
    // Wait for properties to be initialised
    if (startslug === undefined) {
      setTimeout(() => init(), 30);
      return;
    }
    hasInputsSet = true;
    apiService.setBaseUrl(apiurl);
    await initSignUp();
  };
  onMount(() => init());

  const validatePostcode = async () => {
    postcodeValidationMessage = await validationService.validatePostcode(postcode);
    return postcodeValidationMessage;
  };

  const postcodeChanged = async (newPostcode) => {
    const newValue = newPostcode && newPostcode.toUpperCase();
    const valueChanged = postcode !== newValue;
    if (newValue && !valueChanged) return;
    postcode = newValue;
    areAddressesLoading = true;
    await validatePostcode();
    await getAddressesForPostcode(postcode);
    areAddressesLoading = false;
  };

  const validateAddress = () => {
    addressValidationMessage = (
      addresses.find(address => address.id === addressId)
    ) ? null : "Please select an address";
    return addressValidationMessage;
  };

  const addressChanged = (newAddressId) => {
    addressId = newAddressId;
    validateAddress();
  };

  const goToSignUpJourney = () => {
    window.location.href = `${appurl}${startslug}`;
  };

  const getAddressLabel = (address) => {
    const firstPart = [
      address.subBuildingName,
      address.buildingNumber,
      address.buildingName
    ].filter(Boolean).join(", ");
    return [firstPart, address.street].join(" ");
  };

  const onSubmit = async () => {
    isPristine = false;
    const validationFunctions = [
      validatePostcode,
      validateAddress
    ];
    const invalidMessages = (await Promise.all(
      validationFunctions.map(async (func) => {
        const validationError = await func();
        return validationError;
      })
    ));
    if (invalidMessages.some(Boolean)) return;
    isLoading = true;
    try {
      const ssid = cookieService.get(ssidKey);
      const inputs = {
        id: ssid,
        addressId,
        hasAcceptedMarketingConsent,
        hasAcceptedTermsAndConditions: true
      };
      await apiService.saveAddressAndEmailPage(inputs);
      goToSignUpJourney();
    } catch (error) {
      console.log("Error", error);
      submitError = "Our site is currently under maintenance. Please try again later.";
    }
    isLoading = false;
  };
</script>

<style>
  .main {
    display: flex;
    flex-direction: column;
    background: #FFFFFF;
    padding: 21px 20px 29.5px;
    border-radius: 10px;
    text-align: left;
    position: relative;
    overflow: hidden;
  }
  .warning {
    font-size: 1rem;
    line-height: 1.5rem;
    color: #000000;
    background-color: rgba(255, 199, 0, 0.65);
    border-radius: 4px;
    padding: 12px;
    margin: 14px 0 10px;
  }
  .warning-heading {
    font-weight: 700;
  }
  .title {
    color: #111155;
    font-size: 1.125rem;
    line-height: 4rem;
    margin: 0 0 4px;
    display: none;
  }
  swc-input, swc-select {
    margin-top: 10px;
  }
  swc-input:first-of-type {
    margin-top: 0;
  }
  swc-terms-and-conditions {
    margin-top: 12px;
  }
  .loading-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
  }
  swc-primary-button {
    margin-top: 12px;
  }
  swc-feefo-rating {
    margin-top: 14px;
  }
  swc-validation-message {
    display: flex;
    justify-content: center;
  }

  @media (min-width: 768px) {
    .main {
      padding: 9px 30px 29px;
    }
    .title {
      display: block;
    }
    swc-checkbox {
      margin-top: 19px;
    }
    swc-primary-button {
      margin-top: 31px;
    }
  }
</style>

{#if hasInputsSet}
  <div class="main">
    {#if hasWarning}
      <div class="warning">
        {#if warningheading}
          <div class="warning-heading">{warningheading}</div>
        {/if}

        {#if warningcontent}
          <div>{warningcontent}</div>
        {/if}
      </div>
    {/if}

    {#if title}
      <h3 class="title">{title}</h3>
    {/if}
    <swc-input
      autocomplete="postal-code"
      focusoninit={autofocus}
      placeholder="Postcode"
      value={postcode}
      data-e2e-id="postcode-input"
      on:update={(newPostcode) => postcodeChanged(newPostcode.detail)}
      validationmessage={postcodeValidationMessage}
      isformpristine={isPristine}
      hidevalidationonmobile={true} />

    <swc-select
      placeholder="Address"
      data-e2e-id="address-select"
      value={addressId}
      options={addresses.map(address => ({
        value: address.id,
        label: getAddressLabel(address)
      }))}
      on:update={(event) => addressChanged(event.detail)}
      validationmessage={addressValidationMessage}
      isloading={areAddressesLoading}
      isformpristine={isPristine}
      hidevalidationonmobile={true} />

    <swc-terms-and-conditions />

    <swc-primary-button data-e2e-id="submit" on:press={onSubmit} text={buttontext} />
    <swc-feefo-rating></swc-feefo-rating>
    {#if isLoading}
      <div class="loading-wrapper">
        <swc-loading-spinner />
      </div>
    {/if}
    {#if submitError}
      <swc-validation-message message={submitError} />
    {/if}
  </div>
{/if}
